"use client";
import { useEffect } from "react";
import { Box, Typography, Button, Container, Grid, Paper } from "@mui/material";
import {
  Business,
  Group,
  Security,
  Email,
  Phone,
  Event,
} from "@mui/icons-material";

import Header from "../components/HomepageContent/Header";
import Footer from "../components/HomepageContent/Footer";
import Link from "next/link";
import Image from "next/image";
import excellenceIcon from "../../../public/about/excellence-icon.svg";
import innovationIcon from "../../../public/about/innovation-icon.svg";
import collaborationIcon from "../../../public/about/collaboration-icon.svg";
import trustIcon from "../../../public/about/trust-icon.svg";

export default function About() {
  useEffect(() => {
    document.title = "CareAscendium - About";
  }, []);
  const values = [
    {
      icon: excellenceIcon,
      title: "Excellence",
      description:
        "We strive for excellence in everything we do, from our technology to our customer service.",
      color: "#153C7F",
    },
    {
      icon: innovationIcon,
      title: "Innovation",
      description:
        "Continuously innovating to solve complex challenges in healthcare technology.",
      color: "#00B4D8",
    },
    {
      icon: collaborationIcon,
      title: "Collaboration",
      description:
        "Working together with our clients to create solutions that truly make a difference.",
      color: "#28A745",
    },
    {
      icon: trustIcon,
      title: "Trust",
      description:
        "Building trust through transparency, reliability, and consistent delivery.",
      color: "#6F42C1",
    },
  ];

  const milestones = [
    {
      year: "2020",
      title: "Company Founded",
      description:
        "CareAscendium was established with a vision to transform skilled nursing facility operations.",
    },
    {
      year: "2021",
      title: "First Product Launch",
      description:
        "Successfully launched our initial QAPI management platform to select facilities.",
    },
    {
      year: "2022",
      title: "Market Expansion",
      description:
        "Expanded to serve over 100 facilities across multiple states.",
    },
    {
      year: "2023",
      title: "AI Integration",
      description:
        "Introduced advanced AI-powered features for predictive analytics and automation.",
    },
    {
      year: "2024",
      title: "Industry Leader",
      description:
        "Recognized as a leading provider of healthcare technology solutions.",
    },
  ];

  const markers = [
    { top: "75%", left: "20%", Icon: Business, color: "#FFB800" },
    { top: "55%", left: "35%", Icon: Business, color: "#FFB800" },
    { top: "45%", left: "55%", Icon: Business, color: "#E91E63" },
    { top: "35%", left: "72%", Icon: Business, color: "#4CAF50" },
    { top: "20%", left: "88%", Icon: Business, color: "#2196F3" },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#fafafa",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header />

      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(90deg, #2F6FD6 0%, #153C7F 100%)",
          py: { xs: 8, md: 12 },
          px: 4,
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Typography
            variant="h1"
            sx={{
              color: "white",
              mb: 3,
              fontWeight: 700,
              fontSize: { xs: "2.25rem", md: "3.5rem" },
            }}
          >
            About CareAscendium
          </Typography>
          <Typography
            variant="h5"
            sx={{
              mb: 6,
              color: "white",
              maxWidth: "900px",
              mx: "auto",
              lineHeight: 1.6,
              fontWeight: 400,
              fontSize: { xs: "1.1rem", md: "1.5rem" },
            }}
          >
            Transforming skilled nursing facility operations through innovative
            technology and unwavering commitment to quality care.
          </Typography>

          <Box
            sx={{
              maxWidth: 900,
              mx: "auto",
              borderRadius: 6,
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <Image
              src="/about/hero-img.png"
              alt="Caregiver and resident in a warm, natural setting—quality care in action"
              width={900}
              height={520}
              style={{ width: "100%", height: "auto", display: "block" }}
              priority
            />
          </Box>
        </Container>
      </Box>

      {/* Vision & Mission Section */}
      <Box
        sx={{
          bgcolor: "#ECF3FF",
          py: { xs: 6, md: 10 },
          px: 2,
        }}
      >
        <Container maxWidth="lg">
          <Grid container alignItems="center">
            <Grid size={{ xs: 12, md: 6 }} sx={{ order: { xs: 2, md: 1 } }}>
              <Box
                sx={{
                  borderRadius: 12,
                  overflow: "hidden",
                  maxWidth: 360,
                  mx: { xs: "auto", md: 0 },
                }}
              >
                <Image
                  src="/about/our-vision.png"
                  alt="Innovation and technology—ideas in action"
                  width={560}
                  height={360}
                  style={{ width: "100%", height: "auto", display: "block" }}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={{ order: { xs: 1, md: 2 } }}>
              <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
                <Typography
                  variant="h2"
                  sx={{
                    color: "#1a1a1a",
                    fontWeight: 700,
                    fontSize: { xs: "1.5rem", md: "1.75rem" },
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    mb: 2,
                  }}
                >
                  Our Vision
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    lineHeight: 1.7,
                    color: "#333",
                    fontSize: { xs: "1rem", md: "1.125rem" },
                  }}
                >
                  To be the leading technology partner for skilled nursing
                  facilities, empowering them to deliver exceptional care
                  through innovative solutions.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        sx={{
          background:
            "linear-gradient(90deg, #0F2A5A 0%, #153C7F 50%, #1E4A8C 100%)",
          py: { xs: 6, md: 10 },
          px: 2,
        }}
      >
        <Container maxWidth="lg">
          <Grid container alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
                <Typography
                  variant="h2"
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    fontSize: { xs: "1.5rem", md: "1.75rem" },
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    mb: 2,
                  }}
                >
                  Our Mission
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    lineHeight: 1.7,
                    color: "white",
                    fontSize: { xs: "1rem", md: "1.125rem" },
                  }}
                >
                  To provide cutting-edge technology solutions that streamline
                  operations, enhance compliance, and improve resident outcomes
                  in skilled nursing facilities.
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  borderRadius: 12,
                  overflow: "hidden",
                  maxWidth: 360,
                  mx: { xs: "auto", md: 0 },
                  ml: { md: "auto" },
                }}
              >
                <Image
                  src="/about/our-mission.png"
                  alt="Team collaboration and success in the workplace"
                  width={560}
                  height={360}
                  style={{ width: "100%", height: "auto", display: "block" }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Values Section — OUR CORE VALUES */}
      <Box sx={{ bgcolor: "#f5f5f5", py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                color: "#153C7F",
                mb: 2,
                fontWeight: 700,
                fontSize: { xs: "1.75rem", md: "2rem" },
                letterSpacing: "0.05em",
                textTransform: "uppercase",
              }}
            >
              Our Core Values
            </Typography>
            <Typography
              variant="body1"
              sx={{
                maxWidth: "800px",
                mx: "auto",
                lineHeight: 1.6,
                color: "#333",
                fontSize: "1.1rem",
              }}
            >
              The principles that guide everything we do at CareAscendium
            </Typography>
          </Box>

          <Grid container spacing={8}>
            {values.map((value, index) => (
              <Grid size={{ xs: 12, sm: 6 }} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    position: "relative",
                    minHeight: 280,
                    maxWidth: 500,
                    borderRadius: 8,
                    bgcolor: "#fff",
                    boxShadow: "-40px 20px 25px rgba(0,0,0,0.2)",
                    overflow: "hidden",
                  }}
                >
                  {/* Title and description — top-left / central area */}
                  <Box sx={{ pr: "140px", textAlign: "left" }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#1a1a1a",
                        fontWeight: 700,
                        fontSize: "1.6rem",
                        mb: 1.5,
                      }}
                    >
                      {value.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#333",
                        lineHeight: 1.65,
                        fontSize: "1.08rem",
                      }}
                    >
                      {value.description}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 0,
                      width: 130,
                      height: 130,
                      bgcolor: "#153C7F",
                      borderTopLeftRadius: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-20%, -20%)",
                      }}
                    >
                      <Image
                        src={value.icon}
                        alt={value.title}
                        width={48}
                        height={48}
                      />
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Our Journey — title, central SVG (path + markers), cards at bottom */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 1728,
          mx: "auto",
          minHeight: { xs: 720, sm: 900, md: 1200, lg: 2009 },
          py: { xs: 8, sm: 10, md: 14 },
          px: 2,
          background:
            "linear-gradient(168.42deg, rgba(95, 195, 0, 0.91) 4.18%, rgba(54, 102, 7, 0.91) 92.49%)",
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: { xs: 640, sm: 800, md: 880 },
          }}
        >
          <Box sx={{ textAlign: "center", mb: { xs: 8, md: 12 } }}>
            <Typography
              variant="h2"
              sx={{
                color: "#fff",
                mb: 2,
                fontWeight: 700,
                fontSize: { xs: "1.75rem", md: "2rem" },
                letterSpacing: "0.05em",
                textTransform: "uppercase",
              }}
            >
              Our Journey
            </Typography>
            <Typography
              variant="body1"
              sx={{
                maxWidth: "800px",
                mx: "auto",
                lineHeight: 1.6,
                color: "rgba(255,255,255,0.95)",
                fontSize: "1.1rem",
              }}
            >
              From startup to industry leader - the milestones that shaped our
              success
            </Typography>
          </Box>

          {/* Central journey object (winding path + markers) */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: { xs: 280, sm: 360, md: 480 },
              my: { xs: 2, md: 4 },
            }}
          >
            <Box
              component="img"
              src="/about/our-journey-objects.svg"
              alt="Our journey timeline"
              sx={{
                width: "130%",
                maxWidth: 1800,
                height: "auto",
                objectFit: "cover",
                objectPosition: "center",
              }}
            />
          </Box>

          <Grid
            container
            spacing={3}
            sx={{
              mt: "auto",
              pt: { xs: 4, md: 6 },
              alignItems: "stretch",
            }}
          >
            {milestones.map((milestone, index) => (
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                  md: 4,
                }}
                offset={index === 3 ? { xs: 0, sm: 2, md: 2 } : undefined}
                key={index}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 0,
                    height: "100%",
                    minHeight: 220,
                    borderRadius: 3,
                    bgcolor: "#fff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "stretch",
                      bgcolor: "#153C7F",
                    }}
                  >
                    <Box
                      sx={{
                        py: 2,
                        px: 2,
                        minWidth: 56,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: "1rem",
                        }}
                      >
                        {milestone.year}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 2,
                        height: '80%',
                        marginTop: '8px',
                        marginBottom: '8px',
                        bgcolor: "rgba(255,255,255,0.8)",
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ py: 2, px: 2, flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: "0.95rem",
                        }}
                      >
                        {milestone.title}
                      </Typography>
                    </Box>
                  </Box>
                  {/* Bottom section: description */}
                  <Box sx={{ p: 2, flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#333",
                        lineHeight: 1.6,
                        fontSize: "0.9rem",
                      }}
                    >
                      {milestone.description}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Information Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          px: 2,
          bgcolor: "#f0f4f8",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            sx={{
              color: "#1a1a1a",
              fontWeight: 700,
              fontSize: { xs: "1.75rem", md: "2.25rem" },
              textAlign: "center",
              mb: 2,
            }}
          >
            GET IN TOUCH
          </Typography>
          <Typography
            sx={{
              color: "#333",
              fontSize: "1rem",
              textAlign: "center",
              mb: 5,
              lineHeight: 1.6,
            }}
          >
            Ready to transform your skilled nursing facility operations?
            Let&apos;s discuss how CareAscendium can help you achieve your
            goals.
          </Typography>

          <Paper
            elevation={3}
            sx={{
              borderRadius: 8,
              borderBottom: "6px solid #c0c0c0",
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              overflow: "hidden",
              maxWidth: 640,
              mx: "auto",
            }}
          >
            <Box
              sx={{
                bgcolor: "#153C7F",
                color: "white",
                px: 3,
                py: 2,
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.5rem", md: "1.75rem" },
                }}
              >
                Request a Demo
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.95rem",
                  mt: 0.5,
                  opacity: 0.95,
                }}
              >
                See CareAscendium in action
              </Typography>
            </Box>
            <Box
              sx={{
                bgcolor: "#f0f4f8",
                px: 3,
                py: 3,
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "flex-end" },
                justifyContent: "space-between",
                gap: 3,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    color: "#333",
                    fontSize: "0.95rem",
                    lineHeight: 1.6,
                    mb: 2,
                  }}
                >
                  Schedule a personalized demonstration of our platform and
                  discover how we can help your facility improve operations,
                  enhance compliance, and deliver better resident care.
                </Typography>
                <Typography
                  sx={{
                    color: "#333",
                    fontSize: "0.9rem",
                    lineHeight: 1.8,
                    fontWeight: 600,
                  }}
                >
                  info@careascendium.com
                  <br />
                  (555) 123-4567
                  <br />
                  CareAscendium Headquarters
                </Typography>
              </Box>
              <Link href="/contact" style={{ textDecoration: "none" }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Event />}
                  sx={{
                    bgcolor: "#28A745",
                    color: "white",
                    px: 3,
                    py: 1.5,
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      bgcolor: "#218838",
                    },
                  }}
                >
                  Schedule Demo
                </Button>
              </Link>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: 12,
          px: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            py: 10,
            px: 4,
            background: "url(/about/bgdemo.svg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            color: "white",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
            borderRadius: 4,
            maxWidth: 1200,
            mx: "auto",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.12,
              backgroundImage: `
                linear-gradient(90deg, rgba(0, 180, 216, 0.4) 1px, transparent 1px),
                linear-gradient(rgba(0, 180, 216, 0.4) 1px, transparent 1px),
                radial-gradient(circle at 20% 30%, rgba(0, 180, 216, 0.5) 2px, transparent 2px),
                radial-gradient(circle at 80% 70%, rgba(0, 180, 216, 0.5) 2px, transparent 2px)
              `,
              backgroundSize: "40px 40px, 40px 40px, 80px 80px, 80px 80px",
            }}
          />

          <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
            <Typography
              variant="h2"
              sx={{
                mb: 3,
                fontWeight: 800,
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.25rem" },
                textTransform: "uppercase",
                letterSpacing: "0.02em",
              }}
            >
              Sign up for a free demo
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 5,
                opacity: 0.95,
                lineHeight: 1.6,
                fontSize: { xs: "1rem", md: "1.15rem", lg: "1.5rem" },
              }}
            >
              See how CareAscendium helps you reach new levels of Quality Care
              and Teamwork
            </Typography>

            <Link href="/contact" style={{ textDecoration: "none" }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: "#6BBE2D",
                  color: "white",
                  px: 5,
                  py: 2,
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  borderRadius: "50px",
                  textTransform: "none",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
                  "&:hover": {
                    bgcolor: "#5FA827",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Request A Demo
              </Button>
            </Link>
          </Container>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
}
